import React from 'react';
import Dialog from '@mui/material/Dialog';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import styled from 'styled-components';

export interface UsageData {
  plan: string;
  plan_title?: string;
  plan_source?: 'free' | 'subscription' | 'staff';
  model?: string;
  reasoning_efforts?: string[];
  default_reasoning_effort?: string;
  messages: { remaining: number | null; limit: number | null; window_hours: number };
  deep_research: { remaining: number | null; limit: number | null; window_days: number };
  image_generation: { remaining: number | null; limit: number | null; window_hours: number };
}

// The API sends the title; older payloads only carried the id.
const LEGACY_TITLES: Record<string, string> = { free: 'Gratuito', premium: 'Premium' };
const planTitle = (usage: UsageData) => usage.plan_title ?? LEGACY_TITLES[usage.plan] ?? usage.plan;
const quotaText = (remaining: number | null, limit: number | null) =>
  limit == null ? 'Ilimitadas' : `${remaining} de ${limit} restantes`;

interface Props { open: boolean; onClose: () => void; loading: boolean; error: string | null; usage: UsageData | null; }

const Panel = styled.div`
  width: min(92vw, 460px); padding: 24px;
  color: ${({ theme }) => theme.font_color}; background: ${({ theme }) => theme.sidebar_background_solid};
`;
const Header = styled.div`display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 20px;`;
const Title = styled.h2`margin: 0; font: 600 1.2rem 'Noka', sans-serif;`;
const Subtitle = styled.p`margin: 4px 0 0; opacity: 0.65; font-size: 0.88rem;`;
const CloseButton = styled.button`
  border: 0; border-radius: 8px; padding: 5px; display: flex; color: inherit; background: transparent; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
`;
const Plan = styled.div<{ $premium: boolean }>`
  padding: 14px 16px; border-radius: 12px; margin-bottom: 14px; font-weight: 650;
  background: ${({ $premium }) => $premium ? 'rgba(112, 212, 75, 0.14)' : 'rgba(148, 163, 184, 0.14)'};
  border: 1px solid ${({ $premium }) => $premium ? 'rgba(112, 212, 75, 0.45)' : 'rgba(148, 163, 184, 0.32)'};
`;
const QuotaList = styled.div`display: grid; gap: 10px;`;
const QuotaRow = styled.div`
  display: grid; grid-template-columns: 34px 1fr auto; align-items: center; gap: 10px;
  border: 1px solid ${({ theme }) => theme.user_menu_border}; border-radius: 12px; padding: 12px;
`;
const QuotaIcon = styled.div`display: flex; align-items: center; justify-content: center; opacity: 0.72; svg { font-size: 1.2rem; }`;
const QuotaName = styled.div`font-weight: 600; font-size: 0.9rem;`;
const QuotaPeriod = styled.div`opacity: 0.62; font-size: 0.76rem; margin-top: 2px;`;
const QuotaValue = styled.div`font-weight: 650; font-size: 0.86rem; text-align: right;`;
const Center = styled.div`min-height: 180px; display: flex; align-items: center; justify-content: center; text-align: center; color: inherit;`;

const UsageModal: React.FC<Props> = ({ open, onClose, loading, error, usage }) => (
  <Dialog open={open} onClose={onClose} aria-labelledby="usage-modal-title"
    PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden', backgroundImage: 'none' } }}>
    <Panel>
      <Header>
        <div><Title id="usage-modal-title">Uso e plano</Title><Subtitle>Acompanhe as cotas disponíveis na janela atual.</Subtitle></div>
        <CloseButton type="button" aria-label="Fechar" onClick={onClose}><CloseIcon /></CloseButton>
      </Header>
      {loading && <Center><CircularProgress size={28} /></Center>}
      {!loading && error && <Center role="alert">{error}</Center>}
      {!loading && !error && usage && <>
        <Plan $premium={usage.plan !== 'free'}>Plano {planTitle(usage)}</Plan>
        <QuotaList>
          <QuotaRow>
            <QuotaIcon><ChatBubbleOutlineIcon /></QuotaIcon>
            <div>
              <QuotaName>Mensagens</QuotaName>
              {usage.messages.limit != null && (
                <QuotaPeriod>A cada {usage.messages.window_hours} horas</QuotaPeriod>
              )}
            </div>
            <QuotaValue>{quotaText(usage.messages.remaining, usage.messages.limit)}</QuotaValue>
          </QuotaRow>
          <QuotaRow><QuotaIcon><TravelExploreIcon /></QuotaIcon><div><QuotaName>Pesquisa aprofundada</QuotaName><QuotaPeriod>A cada {usage.deep_research.window_days} dias</QuotaPeriod></div><QuotaValue>{quotaText(usage.deep_research.remaining, usage.deep_research.limit)}</QuotaValue></QuotaRow>
          <QuotaRow><QuotaIcon><ImageOutlinedIcon /></QuotaIcon><div><QuotaName>Geração de imagens</QuotaName><QuotaPeriod>A cada {usage.image_generation.window_hours} horas</QuotaPeriod></div><QuotaValue>{quotaText(usage.image_generation.remaining, usage.image_generation.limit)}</QuotaValue></QuotaRow>
        </QuotaList>
      </>}
    </Panel>
  </Dialog>
);

export default UsageModal;
